/**
 * Two-Factor Grace-Period Banner
 * ===============================
 * Persistent top-of-page reminder for users whose role requires 2FA but who
 * have not enrolled yet. Fetches /api/auth/2fa/status once per mount and
 * self-hides when:
 *   - user has 2FA enabled
 *   - user's role is NOT in require2FAForRoles
 *   - status fetch fails (silent fallback — don't scare users with a half-working banner)
 *
 * Clicking the banner routes to /profile/security/2fa.
 *
 * Visual urgency ramps as the grace window closes (amber → red when ≤3 days).
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { getTwoFactorStatus, type TwoFactorStatus } from '../../services/twoFactor.service';

export default function TwoFactorGraceBanner() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    getTwoFactorStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        // Silent — a failing status request must not block the app shell
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);
  // Refetch on path change is cheap and keeps the banner accurate after
  // the user finishes enrollment (at which point it hides).

  if (!status) return null;
  if (status.enabled) return null;
  if (!status.required) return null;

  // Hide banner when the user IS on the enrollment page — it'd be noise there
  if (location.pathname.startsWith('/profile/security/2fa')) return null;

  const days = status.graceDaysRemaining ?? 0;
  const urgent = days <= 3;

  return (
    <Link
      to="/profile/security/2fa"
      className="no-underline block"
      aria-label={
        days === 0
          ? 'Two-step sign-in needs setting up. Last day to set it up. Tap to set it up now.'
          : `Two-step sign-in needs setting up. ${days} day${days === 1 ? '' : 's'} left. Tap to set it up now.`
      }
    >
      <div
        className={[
          'flex items-center gap-2.5 border-b px-4 py-2.5 font-body text-13',
          urgent
            ? 'border-destructive/20 bg-destructive/10 text-destructive'
            : 'border-warning/20 bg-warning/10 text-warning',
        ].join(' ')}
      >
        <AlertTriangle size={16} className="shrink-0" />
        <span className="flex-1">
          Please set up two-step sign-in to keep your account safe.{' '}
          <strong>
            {days === 0
              ? 'Today is the last day.'
              : `${days} day${days === 1 ? '' : 's'} left to set it up.`}
          </strong>
        </span>
        <span className="inline-flex items-center gap-1 font-semibold whitespace-nowrap">
          Set up now <ChevronRight size={14} />
        </span>
      </div>
    </Link>
  );
}
